import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    size: {
      type: String
    },
    closeLabel: {
      type: String,
      attribute: 'close-label'
    }
  }

  constructor() {
    super()
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    document.body.addEventListener('closeModal', this.forceClose.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }

    if (this.size === 'flyout') {
      setTimeout(() => {
        this.open = false
      }, 3000)
    }
  }

  forceClose () {
    this.open = false
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      class="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <button
        @click="${this.close}"
        class="button"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.closeSmall)} ${this.closeLabel}
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(0.5rem);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    @media screen and (max-width: 750px) {
      :host([size='flyout']) {
        max-width: var(--hh-max-width, 320px);
      }
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .dialog {
      position: relative;
      border-radius: var(--hh-button-radius);
      background-color: var(--hh-background);
      top: 50%;
      transform: translateY(-50%);
      margin: auto;
    }

    :host([size='flyout']) .dialog {
      border-radius: 0;
      top: auto;
      transform: initial;
    }

    :host([size='medium']) .dialog {
      max-width: 57.5rem;
    }

    :host([size='auto']) .dialog {
      max-width: fit-content;
    }

    :host([size='medium']) .content,
    :host([size='auto']) .content {
      height: auto;
    }

    .button {
      position: absolute;
      top: var(--hh-space-sm);
      right: var(--hh-space-sm);
      display: flex;
      background-color: transparent;
      border: 0;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0.5rem 0;
      z-index: 2;
      gap: 0.5rem;
    }

    .button svg {
      width: 1rem;
      height: 1rem;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      overflow: auto;
      height: 100vh;
      width: 100%;
      box-sizing: border-box;
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
